import type { WhiteboardContent } from "../app-jotai";
import { loadWhiteboardContent as loadFromFirestore } from "./firestore";

const MAX_CACHE_SIZE = 20;

/**
 * Manages LRU cache for whiteboard content
 */
class WhiteboardContentCache {
  private cache: Map<string, WhiteboardContent>;
  private accessOrder: string[]; // Track access order for LRU

  constructor() {
    this.cache = new Map();
    this.accessOrder = [];
  }

  /**
   * Get content from cache or load from S3
   */
  async get(whiteboardId: string): Promise<WhiteboardContent> {
    // Check cache first
    const cached = this.cache.get(whiteboardId);
    if (cached) {
      // Update access order (move to end = most recently used)
      this.accessOrder = this.accessOrder.filter((id) => id !== whiteboardId);
      this.accessOrder.push(whiteboardId);
      return cached;
    }

    // Load from S3
    const content = await loadFromFirestore(whiteboardId);

    // Store in cache
    this.set(whiteboardId, {
      elements: content.elements,
      files: content.files,
      loadedAt: Date.now(),
    });

    return {
      elements: content.elements,
      files: content.files,
      loadedAt: Date.now(),
    };
  }

  /**
   * Set content in cache (with LRU eviction)
   */
  set(whiteboardId: string, content: WhiteboardContent): void {
    // If cache is full, evict least recently used
    if (this.cache.size >= MAX_CACHE_SIZE && !this.cache.has(whiteboardId)) {
      const lruId = this.accessOrder.shift();
      if (lruId) {
        this.cache.delete(lruId);
        console.log(`[Cache] Evicted LRU whiteboard: ${lruId}`); // eslint-disable-line no-console
      }
    }

    // Add/update cache
    this.cache.set(whiteboardId, content);

    // Update access order
    this.accessOrder = this.accessOrder.filter((id) => id !== whiteboardId);
    this.accessOrder.push(whiteboardId);
  }

  /**
   * Update cached content (when saving)
   */
  update(
    whiteboardId: string,
    elements: any[],
    files: Record<string, any>,
  ): void {
    const cached = this.cache.get(whiteboardId);
    if (cached) {
      this.set(whiteboardId, {
        elements,
        files,
        loadedAt: Date.now(),
      });
    }
  }

  /**
   * Clear entire cache (on profile logout)
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
    console.log("[Cache] Cleared all cached content"); // eslint-disable-line no-console
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: MAX_CACHE_SIZE,
      whiteboardIds: Array.from(this.cache.keys()),
    };
  }
}

// Singleton instance
export const whiteboardCache = new WhiteboardContentCache();
